import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Mic, Trash2, Upload } from "lucide-react";

import Button from "../../../../shared/ui/button/Button";
import { useMeditationAudioUpload } from "../../application/useMeditationAudio";
import { BackgroundTrackId, formatDuration } from "../../domain/types";
import MeditationPlaybackButton from "./MeditationPlaybackButton";
import MeditationStudio from "./MeditationStudio";

type Props = {
    /** The bed the meditation carries, which the studio opens on. */
    backgroundTrack: BackgroundTrackId;
    backgroundTrackId?: string | null;
    voiceVolume?: number;
    backgroundVolume?: number;
    /** Set once a recording is attached, so the form can save it. */
    onAudioReady: (audio: { path: string; durationSeconds: number }) => void;
    onAudioCleared: () => void;
    /** The bed travels with the recording: chosen in the studio, none if uploaded. */
    onBackgroundTrackChange: (
        next: BackgroundTrackId,
        trackId: string | null,
        mix?: { voiceVolume: number; backgroundVolume: number }
    ) => void;
    /** True when the meditation already carries a recording on the server. */
    hasStoredAudio: boolean;
    /** Present once saved, so an upload can be attributed to the right one. */
    meditationId?: string;
    /** A centre meditation: music uploaded in the studio goes to the centre. */
    forCenter?: boolean;
};

/** What a browser will let a therapist pick from disk. */
const ACCEPTED_AUDIO = "audio/mpeg,audio/mp4,audio/wav,audio/webm,audio/ogg,.m4a,.mp3";

/**
 * The two ways a meditation gets its recording.
 *
 * Recording opens the studio, where the take is drawn and the bed is chosen
 * against it. Uploading does not: a file a therapist already has is finished
 * work, mixed however they mixed it, and laying our music under it would be
 * us editing their meditation.
 */
export default function MeditationAudioSection({
    backgroundTrack,
    backgroundTrackId = null,
    voiceVolume,
    backgroundVolume,
    onAudioReady,
    onAudioCleared,
    onBackgroundTrackChange,
    hasStoredAudio,
    meditationId,
    forCenter = false,
}: Props) {
    const { t } = useTranslation();
    const { uploading, error: uploadError, upload } = useMeditationAudioUpload();
    const fileRef = useRef<HTMLInputElement>(null);
    const [studioOpen, setStudioOpen] = useState(false);
    const [attached, setAttached] = useState<{ durationSeconds: number } | null>(null);

    const pickFile = async (file: File) => {
        const duration = await new Promise<number>((resolve) => {
            const probe = new Audio(URL.createObjectURL(file));
            probe.onloadedmetadata = () =>
                resolve(Number.isFinite(probe.duration) ? probe.duration : 0);
            probe.onerror = () => resolve(0);
        });
        const durationSeconds = Math.round(duration);
        const path = await upload(file, meditationId);
        onAudioReady({ path, durationSeconds });
        onBackgroundTrackChange(BackgroundTrackId.NONE, null);
        setAttached({ durationSeconds });
    };

    const clear = () => {
        setAttached(null);
        onAudioCleared();
        onBackgroundTrackChange(BackgroundTrackId.NONE, null);
        if (fileRef.current) fileRef.current.value = "";
    };

    const showsStored = hasStoredAudio && !attached;

    return (
        <section className="rounded-2xl border border-border-subtle bg-surface-raised p-4">
            <h2 className="mb-1 text-sm font-semibold text-content-primary">
                {t("meditation.audio.title")}
            </h2>
            <p className="mb-4 text-xs text-content-tertiary">
                {t("meditation.audio.hint")}
            </p>

            {showsStored && (
                <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-surface-sunken px-3 py-2 text-sm text-content-secondary">
                    <span>{t("meditation.audio.stored")}</span>
                    <span className="flex flex-wrap items-center gap-2">
                        {meditationId && (
                            <MeditationPlaybackButton
                                meditationId={meditationId}
                                backgroundTrack={backgroundTrack}
                                voiceVolume={voiceVolume}
                                backgroundVolume={backgroundVolume}
                            />
                        )}
                        <Button
                            size="sm"
                            variant="ghost"
                            startIcon={<Trash2 size={15} />}
                            onClick={clear}
                        >
                            {t("meditation.audio.remove")}
                        </Button>
                    </span>
                </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
                <Button
                    variant="outline"
                    startIcon={<Mic size={16} />}
                    onClick={() => setStudioOpen(true)}
                >
                    {t(
                        attached || hasStoredAudio
                            ? "meditation.audio.recordAgain"
                            : "meditation.audio.record"
                    )}
                </Button>
                <Button
                    variant="outline"
                    startIcon={<Upload size={16} />}
                    onClick={() => fileRef.current?.click()}
                >
                    {t("meditation.audio.upload")}
                </Button>
                <input
                    ref={fileRef}
                    type="file"
                    accept={ACCEPTED_AUDIO}
                    className="sr-only"
                    onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) void pickFile(file);
                    }}
                />
            </div>

            {attached && (
                <p
                    role="status"
                    className="mt-3 flex items-center justify-between gap-3 text-sm text-status-success"
                >
                    {t("meditation.audio.attached", {
                        duration: formatDuration(attached.durationSeconds),
                    })}
                    <Button
                        size="sm"
                        variant="ghost"
                        startIcon={<Trash2 size={15} />}
                        onClick={clear}
                    >
                        {t("meditation.audio.remove")}
                    </Button>
                </p>
            )}

            {uploading && (
                <p className="mt-3 flex items-center gap-2 text-sm text-content-secondary">
                    <Loader2 size={15} className="animate-spin" aria-hidden="true" />
                    {t("meditation.audio.uploading")}
                </p>
            )}

            {uploadError && (
                <p role="alert" className="mt-3 text-sm text-status-critical">
                    {t("meditation.audio.uploadFailed")}
                </p>
            )}

            <MeditationStudio
                isOpen={studioOpen}
                onClose={() => setStudioOpen(false)}
                backgroundTrack={backgroundTrack}
                backgroundTrackId={backgroundTrackId}
                voiceVolume={voiceVolume}
                backgroundVolume={backgroundVolume}
                meditationId={meditationId}
                forCenter={forCenter}
                onDone={({
                    audio,
                    backgroundTrack: chosen,
                    backgroundTrackId: id,
                    voiceVolume: voice,
                    backgroundVolume: bedLevel,
                }) => {
                    onAudioReady(audio);
                    onBackgroundTrackChange(chosen, id, {
                        voiceVolume: voice,
                        backgroundVolume: bedLevel,
                    });
                    setAttached({ durationSeconds: audio.durationSeconds });
                }}
            />
        </section>
    );
}
